import { useState } from 'react';
import { Button, Input, toast } from '@ethsign/ui';
import { Coins, TrendingUp, ArrowDownToLine, ArrowUpFromLine, AlertCircle, Clock } from 'lucide-react';
import type { UserInfo } from '@/types';
import { TacService } from '@/services/tacService';
import { formatTokenAmount, formatTimeUntilUnlock } from '@/utils/common';
import useSWR from 'swr';
import { useTonConnectUI } from '@tonconnect/ui-react';
import { useTacSdk } from '@/hooks/useTacSdk';
import { GradientButton } from './GradientButton';
import { parseUnits } from 'viem';

interface StakingManagerProps {
  userInfo: UserInfo;
  tacService: TacService;
  onTransactionStart: () => void;
  onTransactionComplete: () => void;
  userTonAddress: string;
  contractAmounts?: { available: bigint; unlocked: bigint } | null;
  unlockInfo?: any;
  isRefreshing: boolean;
  onRefresh: () => void;
}

export function StakingManager({
  userInfo,
  tacService,
  onTransactionStart,
  onTransactionComplete,
  contractAmounts,
  unlockInfo,
  onRefresh
}: StakingManagerProps) {
  const { createSender } = useTacSdk();
  const [tonConnectUI] = useTonConnectUI();
  const [isUndelegating, setIsUndelegating] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isWithdrawingFromAccount, setIsWithdrawingFromAccount] = useState(false);
  const [undelegateAmount, setUndelegateAmount] = useState('');

  // Use contract methods if available, otherwise fall back to userInfo
  const displayUnlocked = contractAmounts?.unlocked ?? userInfo.unlocked;
  const totalStaked = userInfo.userTotalRewards;

  const { data: stakingData, mutate } = useSWR(
    ['rewards', userInfo.stakingAccount, userInfo.validatorAddress],
    async () => {
      try {
        const [rewards, unbondings] = await Promise.all([
          tacService.getDelegationRewards({
            delegatorAddress: userInfo.stakingAccount as `0x${string}`,
            validatorAddress: userInfo.validatorAddress as `0x${string}`
          }),
          tacService.getUnbondingDelegation({
            delegatorAddress: userInfo.stakingAccount as `0x${string}`,
            validatorAddress: userInfo.validatorAddress as `0x${string}`
          })
        ]);
        console.log(unbondings, 'unbondings');
        return {
          rewards,
          unbondings
        };
      } catch (error) {
        console.error('Failed to get delegation rewards:', error);
        throw error;
      }
    }
  );

  const { data: balance, mutate: mutateBalance } = useSWR(['balance', userInfo.stakingAccount], async () => {
    return await tacService.getBalance(userInfo.stakingAccount);
  });

  console.log(stakingData, userInfo, balance, 'stakingData');

  const hasWithdrawableUnbondings = balance?.value && balance?.value > 0;

  const handleClaimRewards = async () => {
    if (!tacService) return;

    setIsClaiming(true);
    onTransactionStart();

    try {
      const sender = await createSender(tonConnectUI);

      const result = await tacService.claimDelegatorRewards(sender);
      toast({
        variant: 'loading',
        title: 'Transaction Pending',
        description: 'Transaction sent, waiting for confirmation...',
        duration: Infinity
      });
      await tacService.waitForTransactionConfirmation(result);

      toast({
        variant: 'success',
        title: 'Transaction Successful',
        description: 'Your rewards have been successfully claimed!'
      });
      onRefresh(); // Refresh all data after successful transaction
      onTransactionComplete();
    } catch (error) {
      console.error('Failed to claim rewards:', error);
      toast({
        variant: 'error',
        title: 'Transaction Failed',
        description: error instanceof Error ? error.message : 'Failed to claim rewards'
      });
    } finally {
      setIsClaiming(false);
    }
  };

  const handleUndelegate = async () => {
    if (!tacService || !undelegateAmount) return;

    const amount = parseUnits(undelegateAmount, 18);

    if (amount > contractAmounts!.available) {
      toast({
        variant: 'error',
        title: 'Transaction Failed',
        description: 'Your undelegation amount is greater than your staked amount'
      });
      return;
    }

    setIsUndelegating(true);
    onTransactionStart();

    try {
      const sender = await createSender(tonConnectUI);

      const result = await tacService.undelegate(amount, sender);
      toast({
        variant: 'loading',
        title: 'Transaction Pending',
        description: 'Transaction sent, waiting for confirmation...',
        duration: Infinity
      });
      const success = await tacService.waitForTransactionConfirmation(result, false);

      if (success) {
        toast({
          variant: 'success',
          title: 'Transaction Successful',
          description: 'Your undelegation has been successfully completed!'
        });
        mutate();
        setUndelegateAmount('');
        onRefresh(); // Refresh all data after successful transaction
        onTransactionComplete();
      }
    } catch (error) {
      console.error('Failed to undelegate:', error);
      toast({
        variant: 'error',
        title: 'Transaction Failed',
        description: error instanceof Error ? error.message : 'Undelegation failed'
      });
    } finally {
      setIsUndelegating(false);
    }
  };

  const handleWithdrawFromAccount = async () => {
    if (!tacService) return;

    setIsWithdrawingFromAccount(true);
    onTransactionStart();

    try {
      const sender = await createSender(tonConnectUI);

      const result = await tacService.withdrawFromAccount(sender);
      toast({
        variant: 'loading',
        title: 'Transaction Pending',
        description: 'Transaction sent, waiting for confirmation...',
        duration: Infinity
      });
      const success = await tacService.waitForTransactionConfirmation(result);

      if (success) {
        toast({
          variant: 'success',
          title: 'Transaction Successful',
          description: 'Withdrawal from account completed successfully!'
        });
        onRefresh(); // Refresh all data after successful transaction
        onTransactionComplete();
        mutateBalance();
      }
    } catch (error) {
      console.error('Failed to withdraw from account:', error);
      toast({
        variant: 'error',
        title: 'Transaction Failed',
        description: error instanceof Error ? error.message : 'Withdrawal from account failed'
      });
    } finally {
      setIsWithdrawingFromAccount(false);
    }
  };

  return (
    <div className="space-y-6 w-full">
      {/* Staking Overview */}
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-slime-600" />
            <h3 className="text-lg font-semibold">Staking Overview</h3>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-muted-foreground">Total Staked</p>
            <p className="text-xl font-bold text-blue-600">{formatTokenAmount(totalStaked)} TAC</p>
          </div>

          <div className="bg-slime-50 p-4 rounded-lg">
            <p className="text-sm text-muted-foreground">Unlocked</p>
            <p className="text-xl font-bold text-slime-600">{formatTokenAmount(displayUnlocked)} TAC</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-muted-foreground">Already Withdrawn</p>
            <p className="text-lg font-medium text-gray-600">{formatTokenAmount(userInfo.withdrawn)} TAC</p>
          </div>

          {unlockInfo && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="h-4 w-4 text-blue-600" />
                <p className="text-sm text-muted-foreground">Next Unlock</p>
              </div>
              {unlockInfo.isFullyVested ? (
                <p className="text-lg font-medium text-slime-600">Fully Vested</p>
              ) : unlockInfo.nextUnlockTime ? (
                <div>
                  <p className="text-lg font-medium text-blue-600">
                    {formatTimeUntilUnlock(unlockInfo.timeUntilUnlock)}
                  </p>
                  <p className="text-xs text-blue-700">{unlockInfo.nextUnlockTime.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">
                    Unlock {unlockInfo.currentStep + 1} of {unlockInfo.totalSteps}
                  </p>
                </div>
              ) : (
                <p className="text-lg font-medium text-gray-600">No vesting schedule</p>
              )}
            </div>
          )}
        </div>

        {/* {validatorInfo && (
          <div className="mt-4 p-4 bg-primary/60  rounded-lg">
            <p className="text-sm text-muted-foreground mb-1">Validator</p>
            <p className="font-medium">{validatorInfo.name}</p>
            <p className="text-sm text-muted-foreground">
              Commission: {validatorInfo.commission} • APY: {validatorInfo.apy}
            </p>
          </div>
        )} */}
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 gap-4">
        {/* Claim Rewards */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Coins className="h-5 w-5 text-yellow-600" />
            <h4 className="font-semibold">Staking Rewards: {stakingData?.rewards} TAC</h4>
          </div>
          <p className="text-sm text-muted-foreground">Claim your accumulated staking rewards from the validator.</p>
          <GradientButton onClick={handleClaimRewards} disabled={isClaiming} className="w-full">
            {isClaiming ? 'Claiming...' : 'Claim Rewards'}
          </GradientButton>
        </div>

        {/* Withdraw from Account */}
      </div>

      {/* Unbonding Status */}
      {stakingData?.unbondings && stakingData.unbondings.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Clock className="h-5 w-5 text-orange-600" />
            <h4 className="font-semibold">Unbonding Status</h4>
          </div>
          <div className="space-y-3">
            {stakingData.unbondings.map((unbonding, index) => {
              const isReady = unbonding.isCompleted && !unbonding.isWithdrawn;
              const isWithdrawn = unbonding.isWithdrawn;
              const completionDate = new Date(unbonding.completionTime * 1000);
              const now = new Date();
              const timeRemaining = completionDate.getTime() - now.getTime();

              return (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${
                    isWithdrawn
                      ? 'bg-gray-50 border-gray-200'
                      : isReady
                      ? 'bg-green-50 border-slime-200'
                      : 'bg-yellow-50 border-yellow-200'
                  }`}
                >
                  <div className="space-y-1">
                    <div className="flex justify-between items-center">
                      <p className="font-medium text-primary">{unbonding.balance} TAC</p>
                      <p className="text-muted-foreground ">{unbonding.completeTimeDisplay}</p>
                    </div>
                    <div className="flex justify-between items-center">
                      <p
                        className={`text-sm ${
                          isWithdrawn ? 'text-gray-600' : isReady ? 'text-slime-600' : 'text-yellow-600'
                        }`}
                      >
                        {isWithdrawn ? 'Withdrawn' : isReady ? 'Ready to withdraw' : 'Unbonding in progress'}
                      </p>
                      {!isWithdrawn && !isReady && timeRemaining > 0 && (
                        <p className="text-sm text-muted-foreground ">
                          {Math.ceil(timeRemaining / (1000 * 60 * 60 * 24))} days remaining
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Undelegate Section */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <ArrowUpFromLine className="h-5 w-5 text-red-600" />
          <h4 className="font-semibold">Undelegate Staking</h4>
        </div>
        <p className="text-sm text-muted-foreground">
          Undelegate your staked TAC tokens. Note that undelegated tokens will have an unbonding period before they can
          be withdrawn.
        </p>
        <div className="space-y-2">
          <div className="relative">
            <Input
              type="number"
              placeholder="Amount to undelegate"
              value={undelegateAmount}
              onChange={(e) => setUndelegateAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
              max={formatTokenAmount(contractAmounts!.available)}
              step="0.000001"
              suffix={
                <button
                  type="button"
                  onClick={() => {
                    setUndelegateAmount(formatTokenAmount(contractAmounts!.available));
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-white"
                >
                  MAX
                </button>
              }
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Total Staked: {formatTokenAmount(contractAmounts!.available)} TAC
          </p>
        </div>
        <Button
          onClick={handleUndelegate}
          disabled={isUndelegating || !undelegateAmount || parseFloat(undelegateAmount) <= 0}
          className="w-full"
          variant="destructive"
        >
          {isUndelegating ? 'Undelegating...' : 'Undelegate'}
        </Button>
        <div className="flex items-start space-x-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-yellow-700">
            Undelegated tokens will be subject to an unbonding period before withdrawal. This process may take several
            days to complete.
          </p>
        </div>
      </div>

      {hasWithdrawableUnbondings ? (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <ArrowDownToLine className="h-5 w-5 text-purple-600" />
            <h4 className="font-semibold">Withdraw from Account</h4>
          </div>
          <p className="text-sm text-muted-foreground">
            Withdraw completed unbonding tokens from your staking account.
          </p>
          <Button onClick={handleWithdrawFromAccount} disabled={isWithdrawingFromAccount} className="w-full">
            {isWithdrawingFromAccount ? 'Withdrawing...' : `Withdraw tokens (${balance?.display} TAC)`}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
